if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        themeBase.onDOMContentLoadedTrigger(() => {
          this.elements = {
            liveRegion: this.querySelector('[id^="GalleryStatus"]'),
            viewer: this.querySelector('[id^="GalleryViewer"]'),
            thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          };
          this.mediaButtons = {
            previous: this.querySelector('.product__media-button--prev'),
            next: this.querySelector('.product__media-button--next'),
          };
          this.mql = window.matchMedia('(min-width: 750px)');
          this.bindMediaButtonEvents();
          if (this.elements.viewer) {
            this.elements.viewer.addEventListener('slideChanged', this.updateMediaButtonsState.bind(this));
          }
          this.updateMediaButtonsState();
          if (!this.elements.thumbnails) return;

          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.updateMediaButtonsState(event.detail.currentElement);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);

        this.elements.viewer.style.height = `${this.elements.viewer.offsetHeight}px`;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');
        this.classList.add('has-active');
        this.elements.viewer.style.height = '';

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            logicalScrollTo(activeMedia.parentElement, {
              left: getLogicalOffsetLeft(activeMedia)
            });
          }
          if (this.closest('quick-add-modal')) return;

          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            let offset = 0;

            if (this.dataset.isProductPage) {
              const stickySidebar = activeMedia.closest('sticky-sidebar');
              offset = stickySidebar && themeBase.isDesktop ? (parseFloat(getComputedStyle(stickySidebar).getPropertyValue('--sticky-top').trim()) || 2) * 10 : 20;
            }

            window.scrollTo({
              top: getLogicalRect(activeMedia).top + window.scrollY - offset,
              behavior: 'smooth'
            });
          }
        });
        this.playActiveMedia(activeMedia);
        this.updateMediaButtonsState(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        logicalScrollTo(this.elements.thumbnails.slider, {
          left: getLogicalOffsetLeft(thumbnail)
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) {
          scriptController.executeWhenScriptLoaded('deferred-media.js', () => {
            deferredMedia.loadContent(false);
          });
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || window.themeHeader?.getWrapper?.() || document.querySelector('.header-sections-wrapper');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      bindMediaButtonEvents() {
        if (this.mediaButtons.previous) {
          this.mediaButtons.previous.addEventListener('click', this.onMediaButtonClick.bind(this, 'previous'));
        }
        if (this.mediaButtons.next) {
          this.mediaButtons.next.addEventListener('click', this.onMediaButtonClick.bind(this, 'next'));
        }
      }

      onMediaButtonClick(direction) {
        const mediaItems = this.getMediaItems();
        if (!mediaItems.length) return;

        const currentMedia = this.getCurrentMediaItem(mediaItems);
        const currentIndex = mediaItems.indexOf(currentMedia);
        const targetIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;

        if (targetIndex < 0 || targetIndex >= mediaItems.length) return;

        this.setActiveMedia(mediaItems[targetIndex].dataset.mediaId, false);
      }

      getMediaItems() {
        if (this.elements.thumbnails) {
          const thumbnailTargets = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')).filter(
            (element) => element.offsetParent !== null
          );
          const mediaFromThumbnails = thumbnailTargets
            .map((thumbnail) => this.elements.viewer.querySelector(`[data-media-id="${thumbnail.dataset.target}"]`))
            .filter((element) => element);

          if (mediaFromThumbnails.length) return mediaFromThumbnails;
        }

        return Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
      }

      getCurrentMediaItem(mediaItems) {
        if (!mediaItems.length) return null;

        const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        if (activeMedia && mediaItems.includes(activeMedia)) return activeMedia;

        if (
          typeof this.elements.viewer.currentPage === 'number' &&
          mediaItems[this.elements.viewer.currentPage]
        ) {
          return mediaItems[this.elements.viewer.currentPage];
        }

        return mediaItems[0];
      }

      updateMediaButtonsState(currentMedia) {
        if (!this.mediaButtons.previous && !this.mediaButtons.next) return;
        if (!this.elements.viewer) return;

        const mediaItems = this.getMediaItems();
        if (!mediaItems.length) return;

        const activeMedia =
          currentMedia && mediaItems.includes(currentMedia) ?
          currentMedia :
          this.getCurrentMediaItem(mediaItems);
        const activeIndex = mediaItems.indexOf(activeMedia);
        const isFirst = activeIndex <= 0 || activeIndex === -1;
        const isLast = activeIndex === -1 ? true : activeIndex >= mediaItems.length - 1;

        this.toggleMediaButtonState(this.mediaButtons.previous, isFirst);
        this.toggleMediaButtonState(this.mediaButtons.next, isLast);
      }

      toggleMediaButtonState(button, disabled) {
        if (!button) return;
        if (disabled) {
          button.setAttribute('disabled', 'disabled');
        } else {
          button.removeAttribute('disabled');
        }
      }
    }
  );
}

if (!customElements.get('zoom-cursor')) {
  customElements.define(
    'zoom-cursor',
    class ZoomCursor extends HTMLElement {
      constructor() {
        super();
        this.listenerOptions = {
          passive: true
        };
        this.onPointerEnter = this.onPointerEnter.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerLeave = this.onPointerLeave.bind(this);
        this.updatePosition = this.updatePosition.bind(this);
        this.updateEnabledState = this.updateEnabledState.bind(this);
      }

      connectedCallback() {
        this.icon = this.querySelector('.product__media-icon');
        this.wrapper = this.closest('.product__modal-opener') || this.parentElement;
        if (!this.icon || !this.wrapper) return;

        this.icon.style.pointerEvents = 'none';
        this.icon.style.willChange = 'transform';

        this.wrapperRect = this.wrapper.getBoundingClientRect();
        window.addEventListener('throttle60fpsResize', this.updateEnabledState, this.listenerOptions);
        this.updateEnabledState();
      }

      disconnectedCallback() {
        this.disableTracking(true);
        window.removeEventListener('throttle60fpsResize', this.updateEnabledState, this.listenerOptions);
      }

      enableTracking() {
        if (this.isTracking || !this.icon || !this.wrapper) return;

        if (window.ResizeObserver && !this.resizeObserver) {
          this.resizeObserver = new ResizeObserver(() => {
            this.wrapperRect = this.wrapper.getBoundingClientRect();
          });
          this.resizeObserver.observe(this.wrapper);
        }

        this.wrapperRect = this.wrapper.getBoundingClientRect();
        this.wrapper.addEventListener('pointerenter', this.onPointerEnter, this.listenerOptions);
        this.wrapper.addEventListener('pointermove', this.onPointerMove, this.listenerOptions);
        this.wrapper.addEventListener('pointerleave', this.onPointerLeave, this.listenerOptions);
        this.isTracking = true;
      }

      disableTracking(resetPosition = false) {
        if (this.wrapper) {
          this.wrapper.removeEventListener('pointerenter', this.onPointerEnter, this.listenerOptions);
          this.wrapper.removeEventListener('pointermove', this.onPointerMove, this.listenerOptions);
          this.wrapper.removeEventListener('pointerleave', this.onPointerLeave, this.listenerOptions);
        }

        if (this.resizeObserver && this.wrapper) {
          this.resizeObserver.unobserve(this.wrapper);
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }

        if (this.rafId) {
          cancelAnimationFrame(this.rafId);
          this.rafId = null;
        }

        this.pendingEvent = null;
        this.isTracking = false;

        if (resetPosition && this.icon) {
          this.icon.style.left = '';
          this.icon.style.top = '';
          this.icon.style.right = '';
          this.icon.style.bottom = '';
          this.icon.style.transform = '';
        }
      }

      updateEnabledState() {
        if (!this.icon || !this.wrapper) return;

        if (window.themeBase.isDesktop) {
          this.enableTracking();
        } else {
          this.disableTracking(true);
        }
      }

      onPointerEnter(event) {
        if (!this.isTracking) return;
        this.wrapperRect = this.wrapper.getBoundingClientRect();
        this.queueUpdate(event);
      }

      onPointerMove(event) {
        if (!this.isTracking) return;
        if (event.pointerType && event.pointerType !== 'mouse') return;
        this.queueUpdate(event);
      }

      onPointerLeave() {
        if (!this.isTracking) return;
        this.pendingEvent = null;
      }

      queueUpdate(event) {
        this.pendingEvent = event;
        if (this.rafId) return;
        this.rafId = requestAnimationFrame(this.updatePosition);
      }

      updatePosition() {
        this.rafId = null;
        if (!this.pendingEvent || !this.wrapperRect) return;

        this.wrapperRect = this.wrapper.getBoundingClientRect();
        const cursorX = this.pendingEvent.clientX - this.wrapperRect.left;
        const cursorY = this.pendingEvent.clientY - this.wrapperRect.top;

        this.icon.style.left = `${cursorX}px`;
        this.icon.style.top = `${cursorY}px`;
        this.icon.style.right = 'auto';
        this.icon.style.bottom = 'auto';
        this.icon.style.transform = 'translate(-50%, -50%)';
      }
    }
  );
}